import _Autocomplete from "@mui/material/Autocomplete";
import _TextField from "@mui/material/TextField";
import _Box from "@mui/material/Box";
import _FormControl from "@mui/material/FormControl";
import _Select from "@mui/material/Select";
import _MenuItem from "@mui/material/MenuItem";
import _Typography from "@mui/material/Typography";
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import debounce from 'lodash/debounce';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCancellablePromise } from '../../hooks';
import { backendAdministrativeLevels, CyclabilityZoneService } from '../../services';
const administrativeLevels = ['region', 'department', 'epci', 'city'];
export const administrativeLevelsMap = {
  region: {
    labelKey: 'commons.administrative_levels.region_one'
  },
  department: {
    labelKey: 'commons.administrative_levels.department_one'
  },
  epci: {
    labelKey: 'commons.administrative_levels.epci_one'
  },
  city: {
    labelKey: 'commons.administrative_levels.city_one'
  }
};
export default function CyclabilityZoneForm({
  eventId,
  cyclabilityZone,
  setCyclabilityZone,
  setAlreadyRegistered
}) {
  const [administrativeLevel, setAdministrativeLevel] = useState('epci');
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState();
  const [loadingChallengesAndContracts, setLoadingChallengesAndContracts] = useState(false);
  const locked = useRef(false);
  const fetch = useMemo(() => debounce(async (search, callback) => {
    if (locked.current) {
      callback([]);
      locked.current = false;
      return;
    }
    const {
      zones: cyclabilityZones
    } = await CyclabilityZoneService.getZones({
      administrativeLevel: backendAdministrativeLevels[administrativeLevel],
      rowsPerPage: 20,
      query: '{ id, administrative_level, reference, country_code, name, geo_polygon_simplified }',
      search,
      getAllItems: false
    });
    callback(cyclabilityZones);
  }, 700), [administrativeLevel]);
  const {
    cancellablePromise,
    cancelPromises
  } = useCancellablePromise();
  const {
    t
  } = useTranslation();
  useEffect(() => {
    return () => {
      cancelPromises();
      setCyclabilityZone?.(null);
      setSearch('');
      setAlreadyRegistered(undefined);
    };
  }, [administrativeLevel]);
  useEffect(() => {
    let active = true;
    setSearchResults(undefined);
    if (!search) return;
    fetch(search, cyclabilityZones => {
      if (active) setSearchResults(cyclabilityZones);
    });
    return () => {
      active = false;
    };
  }, [search, fetch]);
  async function getChallengesAndContracts(cyclabilityZone) {
    setLoadingChallengesAndContracts(true);
    try {
      const [challenges, contracts, territories] = await cancellablePromise(Promise.all([CyclabilityZoneService.getEventChallenges(cyclabilityZone, eventId), CyclabilityZoneService.getPartnerContracts(cyclabilityZone), CyclabilityZoneService.getTerritories(cyclabilityZone)]));
      setAlreadyRegistered(challenges.length > 0 || contracts.length > 0 || territories.length > 0);
      setLoadingChallengesAndContracts(false);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
        setLoadingChallengesAndContracts(false);
      }
    }
  }
  return /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 3
  }, /*#__PURE__*/React.createElement(_Typography, {
    fontWeight: 700,
    variant: "h6"
  }, t('commons.communities.form.cyclability_zone')), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 3
  }, /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: {
      xs: 'column',
      md: 'row'
    },
    flexGrow: 1,
    gap: 3
  }, /*#__PURE__*/React.createElement(_Box, {
    flexShrink: 0,
    width: {
      md: 200
    }
  }, /*#__PURE__*/React.createElement(_FormControl, {
    fullWidth: true,
    margin: "none"
  }, /*#__PURE__*/React.createElement(_Select, {
    disabled: loadingChallengesAndContracts,
    onChange: ({
      target: {
        value
      }
    }) => setAdministrativeLevel(value),
    size: "small",
    value: administrativeLevel
  }, administrativeLevels.map(key => /*#__PURE__*/React.createElement(_MenuItem, {
    key: key,
    value: key
  }, t(administrativeLevelsMap[key].labelKey)))))), /*#__PURE__*/React.createElement(_Box, {
    flexGrow: 1
  }, /*#__PURE__*/React.createElement(_Autocomplete, {
    disablePortal: true,
    fullWidth: true,
    includeInputInList: true,
    disabled: loadingChallengesAndContracts,
    forcePopupIcon: Boolean(search && !cyclabilityZone),
    getOptionKey: option => option.id,
    getOptionLabel: option => option.name,
    loading: Boolean(search) && !cyclabilityZone && searchResults === undefined,
    loadingText: "Chargement",
    noOptionsText: search && t('commons.no_result_found'),
    onChange: (_, value) => {
      locked.current = true;
      cancelPromises();
      setCyclabilityZone?.(value || null);
      setAlreadyRegistered(undefined);
      if (value) getChallengesAndContracts(value);
    },
    onInputChange: (_, value) => {
      setTimeout(() => {
        setSearch(value);
      }, 10);
    },
    options: searchResults || [],
    renderInput: params => /*#__PURE__*/React.createElement(_TextField, _extends({}, params, {
      fullWidth: true,
      label: t('commons.communities.form.cyclability_zone_label'),
      margin: "none",
      size: "small",
      variant: "outlined"
    })),
    renderOption: (props, option) => {
      return /*#__PURE__*/React.createElement(_Box, _extends({}, props, {
        component: "li",
        display: "flex",
        justifyContent: "space-between",
        width: "100%"
      }), /*#__PURE__*/React.createElement(_Typography, {
        noWrap: true,
        variant: "body2"
      }, option.administrativeLevel === 'city' && option.ref ? `${option.name} (${option.ref.substring(0, 2)})` : option.name));
    },
    value: cyclabilityZone || null
  })))));
}